import React, { useState, useRef, useEffect } from "react";
import { uploadImagesToS3, getSignedUrlFromS3, deleteFileFromS3 } from "~/utils/s3Utils";
import { getCustomerId } from "~/components/db/CharacterDBUtils";
import "~/css/Modal.css";
import "~/css/FloorMap.css";
import "~/css/Button.css";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import * as icon from "~/Icons";
import { PlaySound } from "~/SoundUtils";
import { isSettingOpenAtom, volumeAtom } from "~/Atom";
import { floorImagesAtom } from "~/AtomWithDynamoDB";
import { FloorImageType } from "~/Commons";
import SwipeSlider from "~/components/utils/SwipeSlider";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { useIsAwsSettingPage, useIsAwsChatPage } from "~/utils/envUtils";

/**
 * フロアマップモーダルコンポーネント
 */
// eslint-disable-next-line complexity
const FloorMap: React.FC = () => {
  const { t } = useTranslation();
  const [currentFloor, setCurrentFloor] = useState(1);
  // Atomでフロア画像を管理
  const [floorImages, setFloorImages] = useAtom(floorImagesAtom);
  // 画面表示用: 署名付きURL
  const [floorImagesForView, setFloorImagesForView] = useState<FloorImageType[]>([]);
  // スライド方向（アニメーション制御用）
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);
  const [volume] = useAtom(volumeAtom);
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [registeredImageCount, setRegisteredImageCount] = useState(0);
  const isAwsChatPage = useIsAwsChatPage();
  const isAwsSettingPage = useIsAwsSettingPage();
  const [isLocalStorageRef, setIsLocalStorageRef] = useState(false); // ローカルストレージ参照フラグ

  // floorImagesが変化したら署名付きURLを取得してfloorImagesForViewを更新
  useEffect(() => {
    if (process.env.REACT_APP_SYSTEM_REQUIREMENT !== "aws") return;

    // AWS環境でチャット画面の場合、ローカルストレージを参照しているか確認
    if (isAwsChatPage) {
      const localStoredFloorImages = localStorage.getItem("floorImages");
      // ローカルストレージのフロアマップ情報を取得して1件以上あるか確認
      if (localStoredFloorImages) {
        try {
          const parsedImages: FloorImageType[] = JSON.parse(localStoredFloorImages);
          if (parsedImages.length > 0) {
            // ローカルストレージの画像を参照している状態
            setIsLocalStorageRef(true);
          }
        } catch (error) {
          console.error("ローカルストレージのフロアマップデータの解析に失敗しました:", error);
        }
      }
    }

    // フロア画像がない場合は空配列をセットして終了
    if (!floorImages || floorImages.length === 0) {
      setFloorImagesForView([]);
      return;
    }
    // 署名付きURLを取得して表示用にセット
    (async () => {
      try {
        const signedUrls = await Promise.all(
          floorImages.map((img: FloorImageType) => getSignedUrlFromS3(img.src))
        );
        setFloorImagesForView(
          floorImages.map((img: FloorImageType, idx: number) => ({
            src: signedUrls[idx] || img.src,
            name: img.name,
          }))
        );
      } catch (error) {
        console.error("署名付きURLの取得に失敗しました:", error);
      }
    })();
  }, [floorImages, isAwsChatPage]);

  // 階層を切り替える関数（次の階へ）
  const handleNext = () => {
    if (currentFloor < floorImages.length) {
      setDirection("next");
      setCurrentFloor((prev) => prev + 1);
      PlaySound("push", volume);
    }
  };

  // 階層を切り替える関数（前の階へ）
  const handlePrev = () => {
    if (currentFloor > 1) {
      setDirection("prev");
      setCurrentFloor((prev) => prev - 1);
      PlaySound("push", volume);
    }
  };

  /**
   * S3キーを抽出する関数
   * @param images FloorImageTypeの配列
   * @returns S3キーの配列
   * - img.srcがS3キー（http/data:で始まらない場合）はそのまま返す
   * - img.srcが署名付きURL等でS3キーが含まれる場合は正規表現で抽出
   * - それ以外はnull
   */
  const extractS3Keys = (images: FloorImageType[]) => {
    return images
      .map((img) => {
        if (!img.src) return null;
        // data URIや外部URLは除外
        if (img.src.startsWith("http") || img.src.startsWith("data:")) {
          // S3キーがURLに含まれている場合は抽出
          const match = img.src.match(/([\w-]+\/floorImages\/[^?]+)/);
          return match ? match[1] : null;
        }
        // S3キーの場合はそのまま返す
        return img.src;
      })
      .filter(Boolean) as string[];
  };

  /**
   * S3から画像を削除する関数
   * @param keys S3キーの配列
   */
  const deleteImagesFromS3 = async (keys: string[]) => {
    for (const key of keys) {
      await deleteFileFromS3(key);
    }
  };

  // フロアマップの画像を初期化する関数
  const initFloorImages = async () => {
    // 初期化前の画像リストを退避
    const prevFloorImages = [...floorImages];
    const localStoredFloorImages = localStorage.getItem("floorImages");

    setFloorImages([]);
    setCurrentFloor(1);

    // AWS環境の設定画面または、AWS環境のチャット画面かつローカルストレージ参照中
    // かつ、初期化前の画像リストが1件以上ある場合
    if ((isAwsSettingPage || (isAwsChatPage && isLocalStorageRef)) && prevFloorImages.length > 0) {
      // ローカルストレージを参照していない場合（システム全体のフロアマップを参照している状態）
      if (!isLocalStorageRef) {
        const oldKeys = extractS3Keys(prevFloorImages);
        await deleteImagesFromS3(oldKeys);
      } else if (isAwsChatPage && isLocalStorageRef) {
        // チャット画面のローカルストレージを参照している状態
        try {
          const parsedImages: FloorImageType[] = JSON.parse(localStoredFloorImages as string);
          const oldKeys = extractS3Keys(parsedImages);
          await deleteImagesFromS3(oldKeys);
        } catch (error) {
          console.error("ローカルストレージのフロアマップデータの解析に失敗しました:", error);
        }
        // ローカルストレージの削除
        localStorage.removeItem("floorImages");
        setIsLocalStorageRef(false);
      }
    } else {
      // オンプレ環境
      // TODO: public配下の画像削除処理
    }
  };
  // 登録ボタン押下時
  const handleRegisterClick = () => {
    fileInputRef.current?.click();
    PlaySound("push", volume);
  };
  // 初期化ボタン押下時
  const handleInitClick = () => {
    // 初期化確認
    if (!window.confirm(t("initMessage"))) return;

    initFloorImages();
    PlaySound("push", volume);
    // DynamoDBに登録されている画像に切り替えるためリロードを促す
    if (isAwsChatPage) {
      alert(t("reloadForInit"));
      window.location.reload();
    }
  };

  // フロアマップ更新
  // eslint-disable-next-line complexity
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    // ファイルが選択されていない場合、または選択されたファイルが0個の場合は何もしない
    if (!files || files.length === 0) return;

    // ファイルをソートして、名前順に並べ替え
    const sortedFiles = Array.from(files).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );

    try {
      let images: FloorImageType[] = [];
      if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws") {
        // customerIdを取得
        const customerId = await getCustomerId();

        // S3アップロード
        const s3Keys = await uploadImagesToS3(sortedFiles, "floorImages", customerId);
        // DynamoDB(atom)にはS3キーをsrcとして格納、nameは元ファイル名
        images = s3Keys.map((key, idx) => ({
          src: key,
          name: sortedFiles[idx].name.replace(/\.[^/.]+$/, ""),
        }));
      } else {
        // DataURLでローカル保存
        // TODO: public配下に保存する処理に変更する
        images = await Promise.all(
          sortedFiles.map((file) => {
            return new Promise<FloorImageType>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const replaceFileName = file.name.replace(/\.[^/.]+$/, ""); // 拡張子を除去
                resolve({ src: reader.result as string, name: replaceFileName });
              };
              reader.readAsDataURL(file);
            });
          })
        );
      }

      // ファイル読み込みまたはアップロードが成功した場合のみ、既存画像を削除して新しい画像を設定
      if (images.length > 0) {
        // 古いfloorImagesを退避
        const prevFloorImages = [...floorImages];

        // 新しい画像リストを設定
        setFloorImages(images);

        // 旧ファイルを削除（AWS環境のみ）
        if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws" && prevFloorImages.length > 0) {
          // Web管理画面またはチャット画面でローカルストレージ参照時は古い画像を削除
          if (!isAwsChatPage || isLocalStorageRef) {
            const oldKeys = extractS3Keys(prevFloorImages);
            await deleteImagesFromS3(oldKeys);
          }
        }
        setCurrentFloor(1); // 最初のフロアに戻す

        // 成功通知を表示
        setRegisteredImageCount(images.length);
        setIsSuccessModalOpen(true);

        // ファイル選択をリセットして、同じファイルでも再選択できるようにする
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("ファイルの読み込みまたはアップロードに失敗しました:", error);
    }
  };

  // 成功モーダルを閉じる
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    PlaySound("push", volume);
  };

  const activeFloorImages =
    process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws" ? floorImagesForView : floorImages;

  return (
    <>
      {isSettingOpen && (
        <div className="file-input-container">
          <button
            className={`button-0 flexible ${!isLocalStorageRef && isAwsChatPage ? "disabled-btn" : ""}`}
            onClick={handleInitClick}
            disabled={!isLocalStorageRef && isAwsChatPage}
          >
            {t("reset")}
          </button>
          <button className="button-1 flexible" onClick={handleRegisterClick}>
            {floorImages.length > 0 ? t("update") : t("register")}
          </button>
        </div>
      )}

      <div className="modal-content">
        <div className="image-name">
          {activeFloorImages.length > 0 && activeFloorImages[currentFloor - 1]?.name}
        </div>
        <div className={`image-container ${direction}`}>
          {activeFloorImages.length > 0 ? (
            <SwipeSlider
              currentIndex={currentFloor - 1}
              onIndexChange={(newIndex) => setCurrentFloor(newIndex + 1)}
              className="swipe-slider-container"
            >
              {activeFloorImages.map((items, idx) => (
                <img key={idx} src={items.src} alt={items.name} />
              ))}
            </SwipeSlider>
          ) : (
            <h3>{t("noFloorMap")}</h3>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {/* 左右のボタンを配置 */}
          <button
            className="btn btn-circle floor-nav-button prev"
            onClick={handlePrev}
            disabled={currentFloor === 1 || floorImages.length <= 1}
            aria-label={t("previousFloor")}
          >
            <icon.icons_arrow_left />
          </button>

          <button
            className="btn btn-circle floor-nav-button next"
            onClick={handleNext}
            disabled={currentFloor === floorImages.length || floorImages.length <= 1}
            aria-label={t("nextFloor")}
          >
            <icon.icons_arrow_right />
          </button>
        </div>
      </div>

      {/* 登録成功通知モーダル */}
      <ConfirmationModal
        show={isSuccessModalOpen}
        handleClose={handleSuccessModalClose}
        handleConfirm={handleSuccessModalClose}
        title={t("registerSuccess")}
        body={t("registerSuccessMessage", { count: registeredImageCount })}
        cancelText=""
        confirmText="OK"
        variant="success"
      />
    </>
  );
};

export default FloorMap;
